import { Navigate, Outlet, useLoaderData, useLocation } from 'react-router-dom';
import { ROUTES } from '../../types/routes';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
	selectAppSettings,
	selectBiometry,
	selectIsTelegram,
	selectLocale,
	selectPreviousMainLocation,
	selectTheme,
	setBiometry,
	setIsTelegram,
} from '../../services/root';
import { useTelegram } from '../../hooks/useTelegram';
import { useEffect } from 'react';
import { selectCountriesDictionary, setDictionary } from '../../services/dictionaries';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import {
	selectAccessToken,
	selectIsAuthenticated,
	selectUserIsActive,
	setUserIsActive,
} from '../../services/auth';
import { TGlobalRouteLoaderResponse } from '../../types/types';
import { useGetUserRests } from '../../data/mutations/wallet';
import { selectIsVerified } from '../../services/user';
import { mapRegistrationStepId, setDefaultDictionaries } from '../../utils';
import { useGetMarketPrices } from '../../data/mutations/market';
import {
	selectRegistrationInfo,
	selectRegistrationIsPageInited,
	selectRegistrationState,
	setRegistrationBackCheckboxes,
	setRegistrationCountry,
	setRegistrationEmail,
	setRegistrationFullPhone,
	setRegistrationPhone,
} from '../../services/registration';
import {
	useGetStateInfoMutation,
	useRegistrationGetEula,
	useRegistrationStartMutation,
} from '../../data/mutations/registration';
import { useIdleTimer } from 'react-idle-timer';
import { useRevokeTokenMutation } from '../../data/mutations/profile';

const ProtectedRoute = () => {
	const biometry = useAppSelector(selectBiometry);
	const appSettings = useAppSelector(selectAppSettings);
	const isTelegram = useAppSelector(selectIsTelegram);

	const accessToken = useAppSelector(selectAccessToken);
	const isActive = useAppSelector(selectUserIsActive);
	const locale = useAppSelector(selectLocale);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);

	const dispatch = useAppDispatch();
	const data = useLoaderData();
	const navigate = useCustomNavigate();

	const { logout: revokeToken } = useRevokeTokenMutation();

	const onIdle = () => {
		dispatch(setUserIsActive(false));
	};

	const mapLogoutTime = () => {
		if (biometry?.isBiometrySetup && isTelegram) {
			return appSettings?.quickTimeout;
		}
		return appSettings?.loginTimeout;
	};

	//eslint-disable-next-line
	const {} = useIdleTimer({
		onIdle,
		timeout: mapLogoutTime(),
		throttle: 500,
	});

	useEffect(() => {
		if (!isActive) {
			revokeToken({
				accessToken: accessToken as string,
				dispatch: dispatch,
			});
		}
	}, [isActive]);

	useEffect(() => {
		if (!isAuthenticated) {
			if(biometry?.isBiometrySetup && isTelegram) {
				sessionStorage.setItem('tempPreviousUrl', window.location.pathname);
			}
			window.location.replace(ROUTES.AUTH.INDEX.replace(':locale', locale));
		}
	}, [isAuthenticated]);

	useEffect(() => {
		setDefaultDictionaries(data as TGlobalRouteLoaderResponse, dispatch, navigate);
	}, []);

	if (!isAuthenticated) {
		return <Navigate to={ROUTES.AUTH.INDEX.replace(':locale', locale)} />;
	}

	return <Outlet />;
};

const CommonRoute = () => {
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const previousMainLocation = useAppSelector(selectPreviousMainLocation);
	const navigate = useCustomNavigate();

	useEffect(() => {
		if (isAuthenticated) {
			navigate(
				previousMainLocation != '' ? previousMainLocation : ROUTES.WALLET.INDEX
			);
		}
	}, []);

	return <Outlet />;
};

const GlobalRoute = () => {
	const theme = useAppSelector(selectTheme);
	const dispatch = useAppDispatch();

	const { isTelegram, biometricManager } = useTelegram();

	const handleIsInitBiometry = () => {
		biometricManager.init(() => {
			dispatch(
				setBiometry({
					isBiometricAvailable: biometricManager.isBiometricAvailable,
					isBiometryInited: true,
				})
			);
		});
	};

	useEffect(() => {
		if (theme === 'dark') {
			document.body.setAttribute('data-theme', theme?.toLowerCase());
		} else {
			document.body.removeAttribute('data-theme');
		}
	}, [theme]);

	useEffect(() => {
		if (isTelegram) {
			dispatch(setIsTelegram(isTelegram));
			handleIsInitBiometry();
		}
	}, []);

	return <Outlet />;
};

const ProfileRoute = () => {
	const dispatch = useAppDispatch();
	const data = useLoaderData();

	useEffect(() => {
		dispatch(
			setDictionary(
				//eslint-disable-next-line
				Object.assign(data as any, {
					dictionaryName: 'countries',
				})
			)
		);
	}, []);

	const isVerified = useAppSelector(selectIsVerified);
	//eslint-disable-next-line
	const {} = useGetUserRests(isVerified);
	//eslint-disable-next-line
	const {} = useGetMarketPrices();

	return <Outlet />;
};

const PaymentsRoute = () => {
	const isVerified = useAppSelector(selectIsVerified);
	//eslint-disable-next-line
	const {} = useGetUserRests(isVerified);
	//eslint-disable-next-line
	const {} = useGetMarketPrices();

	return <Outlet />;
};

const RegistrationRoute = () => {
	const state = useAppSelector(selectRegistrationState);
	const stateInfo = useAppSelector(selectRegistrationInfo);
	const isPageInited = useAppSelector(selectRegistrationIsPageInited);
	const countries = useAppSelector(selectCountriesDictionary);

	const location = useLocation();
	const navigate = useCustomNavigate();
	const dispatch = useAppDispatch();
	const data = useLoaderData();

	const { startRegistration } = useRegistrationStartMutation(
		navigate,
		isPageInited,
		dispatch
	);

	const { getRegistrationEula } = useRegistrationGetEula();

	const { getStateInfo } = useGetStateInfoMutation(dispatch);

	useEffect(() => {
		if (state === '') {
			startRegistration();
		}
		dispatch(
			setDictionary(
				//eslint-disable-next-line
				Object.assign(data as any, {
					dictionaryName: 'countries',
				})
			)
		);
	}, []);

	useEffect(() => {
		if (state?.length > 0) {
			if (!isPageInited) {
				getRegistrationEula({
					state: state,
					dispatch: dispatch,
				});
			}
			getStateInfo({
				state: state,
			});
		}
	}, [state, location]);

	useEffect(() => {
		if (stateInfo && countries) {
			if (stateInfo?.email != '') {
				dispatch(setRegistrationEmail(stateInfo?.email));
			}
			if (stateInfo?.phone != '') {
				dispatch(
					setRegistrationPhone(
						stateInfo?.phone.replace(
							countries?.dictionary?.find(
								(country) => country?.code === stateInfo?.phoneCountryCode
							)?.phoneCode as string,
							''
						)
					)
				);
				dispatch(
					setRegistrationCountry({
						code: countries?.dictionary?.find(
							(country) => country?.code === stateInfo?.phoneCountryCode
						)?.phoneCode,
						imgUrl: countries?.dictionary?.find(
							(country) => country?.code === stateInfo?.phoneCountryCode
						)?.link,
					})
				);
				dispatch(setRegistrationFullPhone(stateInfo?.phone));
			}

			dispatch(setRegistrationBackCheckboxes(stateInfo?.checkBoxesResult));
			if (!isPageInited) {
				mapRegistrationStepId(stateInfo?.currentStepId, navigate, dispatch);
			}
		}
	}, [stateInfo, countries]);

	return <Outlet />;
};

const VerificationRoute = () => {
	const dispatch = useAppDispatch();
	const data = useLoaderData();

	useEffect(() => {
		dispatch(
			setDictionary(
				//eslint-disable-next-line
				Object.assign(data as any, {
					dictionaryName: 'countries',
				})
			)
		);
	}, []);

	return <Outlet />;
};

const ExchangeRoute = () => {
	const dispatch = useAppDispatch();
	const data = useLoaderData();

	useEffect(() => {
		dispatch(
			setDictionary(
				//eslint-disable-next-line
				Object.assign(data as any, {
					dictionaryName: 'assetSymbols',
				})
			)
		);
	}, []);

	const isVerified = useAppSelector(selectIsVerified);
	//eslint-disable-next-line
	const {} = useGetUserRests(isVerified);
	//eslint-disable-next-line
	const {} = useGetMarketPrices();

	return <Outlet />;
};

export {
	RegistrationRoute,
	ProtectedRoute,
	CommonRoute,
	GlobalRoute,
	ProfileRoute,
	PaymentsRoute,
	ExchangeRoute,
	VerificationRoute,
};
